<template>
  <basic-dialog
    :show="show"
    @close="$emit('close')"
    fullscreen
    :close-disabled="actionBtnsDisabled"
  >
    <template v-slot:title>{{ title }}</template>
    <v-container py-0 fluid>
      <v-row justify="center" align="center">
        <v-col>
          <supplier-list-select
            item-value="id"
            v-model="selectedSupplier"
            :outlined="outlinedInputs"
            dense
            hide-details
            :disabled="supplierDisabled"
            clearable
            @clear="onSupplierClear"
          />
        </v-col>
        <v-col>
          <head-office-list-select
            :disabled="headOfficeDisabled"
            :outlined="outlinedInputs"
            hide-details
            dense
            v-model="selectedHeadOffice"
            clearable
            @clear="onHeadOfficeClear"
          />
        </v-col>
        <v-col>
          <v-text-field
            label="Supplier Inv Number"
            v-model="supplierInvoiceNumber"
            :outlined="outlinedInputs"
            hide-details
            dense
          />
        </v-col>
        <v-col>
          <date-picker
            v-model="invoiceDate"
            custom-label="Invoice Date"
            :outlined="outlinedInputs"
            hide-details
            dense
          />
        </v-col>
        <v-col>
          <date-picker
            v-model="invoiceDueDate"
            custom-label="Invoice Due Date"
            :outlined="outlinedInputs"
            hide-details
            dense
          />
        </v-col>

        <h3 class="h5">INVOICED:</h3>

        <v-col cols="1">
          <v-text-field
            label="Net Amount"
            v-model.number="netAmount"
            :outlined="outlinedInputs"
            hide-details
            dense
            @change="onNetAmountChange"
          />
        </v-col>
        <v-col cols="1">
          <v-text-field
            label="Vat Amount"
            v-model.number="vatAmount"
            :outlined="outlinedInputs"
            hide-details
            dense
            @change="onVatAmountChange"
          />
        </v-col>
        <v-col cols="1">
          <v-text-field
            label="Total Amount"
            v-model.number="totalAmount"
            :outlined="outlinedInputs"
            hide-details
            dense
            disabled
          />
        </v-col>
      </v-row>
    </v-container>
    <div v-if="debug">
      <code>
        {{ invoiceDate }}
      </code>
    </div>
    <v-divider></v-divider>
    <h4 class="overline">Filters</h4>
    <purchase-invoice-list
      hide-title
      hide-ag-grid
      :multi-select="false"
      show-approve
      hide-remove-item
      :edit-item-cb="onPurchaseInvoiceEdit"
      @approve="onApproveWithoutBreakdown"
      show-filters
      :custom-filters="piFilters"
      :hide-filters="['supplier_name_contains', 'headofficeId']"
      for-pi-approvals
      refName="piApproveInvoicesFilters"
      refNameColumns="piApproveInvoicesCols"
      :fixedHeader="false"
    ></purchase-invoice-list>
    <v-card outlined v-if="id != ''">
      <v-card-title>
        <h5>Purchase Invoice Payments/Approvals</h5>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="approvalsHeaders"
          :items="items"
          :loading="$apollo.loading"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              dark
              small
              color="red"
              @click="onUnApprove(item)"
              :disabled="posted"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" small>mdi-close</v-icon>
                </template>
                Un-Approve
              </v-tooltip>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-row align="center" justify="end">
      <h3 class="h5">APPROVED:</h3>
      <v-col cols="1">
        <v-text-field
          label="Net Amount"
          v-model.number="approvedNetAmount"
          :outlined="outlinedInputs"
          hide-details
          dense
          disabled
        />
      </v-col>
      <v-col cols="1">
        <v-text-field
          label="Vat Amount"
          v-model.number="approvedVatAmount"
          :outlined="outlinedInputs"
          hide-details
          dense
          disabled
        />
      </v-col>
      <v-col cols="1">
        <v-text-field
          label="Total Amount"
          v-model.number="approvedTotalAmount"
          :outlined="outlinedInputs"
          hide-details
          dense
          disabled
        />
      </v-col>
    </v-row>
    <v-row align="center" justify="end">
      <h3 class="h5">BALANCE:</h3>
      <v-col cols="1">
        <v-text-field
          label="Net Amount"
          v-model.number="balanceApprovedNetAmount"
          :outlined="outlinedInputs"
          hide-details
          dense
          disabled
          @change="onNetAmountChange"
        />
      </v-col>
      <v-col cols="1">
        <v-text-field
          label="Vat Amount"
          v-model.number="balanceApprovedVatAmount"
          :outlined="outlinedInputs"
          hide-details
          dense
          disabled
        />
      </v-col>
      <v-col cols="1">
        <v-text-field
          label="Total Amount"
          v-model.number="balanceApprovedTotalAmount"
          :outlined="outlinedInputs"
          hide-details
          dense
          disabled
        />
      </v-col>
    </v-row>
    <vat-breakdown-dialog
      :show="showVatEditor"
      @close="showVatEditor = false"
      v-model="selectedInvoice"
      @approve="onApprove"
      :key="vatBreakdownRefreshKey"
    />
    <template v-slot:actions-left>
      <v-btn
        text
        color="warning"
        class="mr-2"
        @click="onCancel"
        v-if="id != '' && items.length == 0"
        >Remove Approval</v-btn
      >
      <v-btn
        text
        color="secondary"
        class="mr-2"
        @click="onNewInvoice"
        :disabled="actionBtnsDisabled"
        >New Invoice</v-btn
      >
      <v-btn
        text
        color="secondary"
        class="mr-2"
        @click="onNextInvoice"
        :disabled="actionBtnsDisabled"
        >Next Invoice</v-btn
      >
      <v-switch
        v-model="posted"
        label="Post"
        @change="onPost"
        :disabled="posted || notBalanaced"
      />
    </template>
    <basic-dialog
      :show="showSupplierInvoiceNumberDialog"
      @close="showSupplierInvoiceNumberDialog = false"
      max-width="600"
    >
      <template v-slot:title>Supplier Invoice Number</template>
      <v-text-field
        v-model="supplierInvoiceNumber"
        label="Invoice Number"
        :outlined="outlinedInputs"
        hide-details
      />
      <template v-slot:actions>
        <v-btn
          color="primary"
          :disabled="supplierInvoiceNumber == ''"
          @click="onSuppInvoiceNoContinue"
          >Continue</v-btn
        >
      </template>
    </basic-dialog>
  </basic-dialog>
</template>

<script>
import gql from "graphql-tag";
import BasicDialog from "../../shared/BasicDialog.vue";
import DatePicker from "../../shared/DatePicker.vue";
import PurchaseInvoiceList from "../purchase-invoices/PurchaseInvoiceList.vue";
import VatBreakdownDialog from "./VatBreakdownDialog.vue";
import { removeGQLTypename } from "../../../../helpers";
import EventBus from "../../../../store/event-bus";
import SupplierListSelect from "../../shared/SupplierListSelect.vue";
import HeadOfficeListSelect from "../../shared/HeadOfficeListSelect.vue";
import DefaultListActions from "../../shared/DefaultListActions.vue";

const moment = require("moment");
const PI_VAT_BREAKDOWN = gql`
  query piVATBreakdown($id: ID!) {
    purchaseInvoice(id: $id) {
      id
      vatAmount
      netAmount
      amount
      vatBreakdown {
        vatCode
        vatAmount
        vatPercent
        netAmount
        grossAmount
      }
    }
  }
`;
const CREATE_PI_APPROVALS = gql`
  mutation createPIapprovals($input: PiApprovalsInput!) {
    createPiApprovals(input: $input) {
      id
      name
      invoiceDate
      dueDate
      reference
      netAmount
      vatAmount
      totalAmount
      supplierName
      supplierId
      currency
      createdAt
      notes
      exported
    }
  }
`;
const UPDATE_APPROVALS = gql`
  mutation updatePIapprovals($input: PiApprovalsInput!) {
    updatePiApprovals(input: $input) {
      id
      name
      invoiceDate
      dueDate
      reference
      netAmount
      vatAmount
      totalAmount
      supplierName
      supplierId
      currency
      createdAt
      notes
      exported
    }
  }
`;
const APPROVAL_PAYMENTS_LIST = gql`
  query approvalPayments($id: ID) {
    purchaseInvoicePayments(filter: { pi_approval_id: $id }) {
      edges {
        node {
          id
          salesforceId
          amount
          supplierName
          itineraryReference
          reference
          amount
          exported
          externalReference
          payeeName
          batchNo
          netAmount
          vatAmount
          piNumber
          taxCode
        }
      }
    }
  }
`;
const UNAPPROVE = gql`
  mutation unapprove($id: ID!) {
    unapprovePayment(id: $id) {
      success
      error
      message
    }
  }
`;
import { PURCHASE_INVOICE_PAYMENT_DELETE_MUTATION } from "../../../../constants/graphql";
const randomize = require("randomatic");
export default {
  name: "purchase-invoice-approval-dialog",
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {
    BasicDialog,
    PurchaseInvoiceList,
    DatePicker,
    VatBreakdownDialog,
    SupplierListSelect,
    HeadOfficeListSelect,
    DefaultListActions,
  },

  data() {
    return {
      debug: false,
      id: this.value ? this.value.id : "",
      selectedSupplier:
        this.value && this.value.headOfficeId
          ? ""
          : this.value
          ? this.value.supplierId
          : "",
      selectedHeadOffice:
        this.value && this.value.headOfficeId ? this.value.headOfficeId : "",
      panels: [0],
      supplierInvoiceNumber: this.value ? this.value.name : "",
      invoiceDate: this.value
        ? this.value.invoiceDate
        : moment(new Date()).format("YYYY-MM-DD"),
      invoiceDueDate: this.value
        ? this.value.dueDate
        : moment(new Date()).format("YYYY-MM-DD"),
      netAmount: this.value ? this.value.netAmount : 0,
      vatAmount: this.value ? this.value.vatAmount : 0,
      totalAmount: this.value ? Number(this.value.totalAmount).toFixed(2) : 0,
      posted: this.value ? this.value.exported : false,
      showVatEditor: false,
      selectedInvoice: null,
      approvalsHeaders: [
        {
          text: "Batch ID",
          value: "salesforceId",
          name: "salesforceId",
        },
        {
          text: "Iti Ref",
          value: "itineraryReference",
          name: "itineraryReference",
        },
        {
          text: "Supplier",
          value: "supplierName",
          name: "supplierName",
        },
        {
          text: "Head Office",
          value: "headOfficeName",
          sortable: false,
          name: "headOfficeName",
        },
        {
          text: "PI Inv. Number",
          value: "piNumber",
          name: "piNumber",
        },
        {
          text: "Supplier Invoice Number",
          value: "externalReference",
          name: "externalReference",
        },
        {
          text: "Net Amount",
          value: "netAmount",
          name: "netAmount",
        },

        {
          text: "VAT Amount",
          value: "vatAmount",
          name: "vatAmount",
        },

        {
          text: "Gross Amount",
          value: "amount",
          name: "amount",
        },
        {
          text: "VAT Code",
          value: "taxCode",
          name: "taxCode",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
      showSupplierInvoiceNumberDialog: false,
      handlerToConfirm: null,
      vatBreakdownRefreshKey: randomize("Aa0", 32),
      checkAndApprove: false,
      actionBtnsDisabled: false,
    };
  },

  computed: {
    notBalanaced() {
      return (
        this.approvedTotalAmount == 0 ||
        this.balanceApprovedNetAmount != 0 ||
        this.balanceApprovedVatAmount != 0 ||
        this.balanceApprovedTotalAmount != 0
      );
    },
    piFilters() {
      return this.payeeSelected
        ? {
            supplier_id:
              this.selectedSupplier && this.selectedSupplier != ""
                ? this.selectedSupplier
                : undefined,
            head_office_id:
              this.selectedHeadOffice && this.selectedHeadOffice != ""
                ? this.selectedHeadOffice
                : undefined,
          }
        : {};
    },
    payeeSelected() {
      return this.selectedSupplier != "" || this.selectedHeadOffice != "";
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    title() {
      return this.value
        ? `PI Approval - ${this.value.supplierName}`
        : "PI Approval";
    },
    items() {
      return this.purchaseInvoicePayments && this.purchaseInvoicePayments.edges
        ? this.purchaseInvoicePayments.edges.map(removeGQLTypename)
        : [];
    },
    approvedNetAmount() {
      return this.id != "" && this.items.length
        ? this.items
            .reduce((t, item) => t + Number(item.netAmount), 0)
            .toFixed(2)
        : 0;
    },
    approvedVatAmount() {
      return this.id != "" && this.items.length
        ? this.items
            .reduce((t, item) => t + Number(item.vatAmount), 0)
            .toFixed(2)
        : 0;
    },
    balanceApprovedNetAmount() {
      return (Number(this.netAmount) - Number(this.approvedNetAmount)).toFixed(
        2
      );
    },
    balanceApprovedVatAmount() {
      return (Number(this.vatAmount) - Number(this.approvedVatAmount)).toFixed(
        2
      );
    },
    balanceApprovedTotalAmount() {
      return (this.totalAmount - this.approvedTotalAmount).toFixed(2);
    },
    approvedTotalAmount() {
      return (
        Number(this.approvedNetAmount) + Number(this.approvedVatAmount)
      ).toFixed(2);
    },
    supplierDisabled() {
      return (
        this.selectedHeadOffice != undefined && this.selectedHeadOffice != ""
      );
    },
    headOfficeDisabled() {
      return this.selectedSupplier != undefined && this.selectedSupplier != "";
    },
  },

  watch: {
    value(newVal) {
      this.supplierInvoiceNumber = newVal ? newVal.name : "";
      this.invoiceDate = newVal ? newVal.invoiceDate : "";
      this.invoiceDueDate = newVal ? newVal.dueDate : "";
      this.netAmount = newVal ? newVal.netAmount : 0;
      this.vatAmount = newVal ? newVal.vatAmount : 0;
      this.totalAmount = newVal ? newVal.totalAmount : 0;
      this.posted = newVal.exported.toString() == "true";
      this.id = newVal ? newVal.id : "";
      if (newVal.headOfficeId) {
        this.selectedSupplier = "";
        this.selectedHeadOffice = newVal.headOfficeId;
      } else {
        this.selectedSupplier = newVal ? newVal.supplierId : "";
        this.selectedHeadOffice = "";
      }
    },
  },

  methods: {
    onSuppInvoiceNoContinue() {
      this.showSupplierInvoiceNumberDialog = false;
      if (this.handlerToConfirm) {
        this.handlerToConfirm();
      }
    },
    onHeadOfficeClear() {
      this.selectedHeadOffice = "";
    },
    onSupplierClear() {
      this.selectedSupplier = "";
      // console.log(
      //   "selected supp",
      //   this.selectedSupplier,
      //   `-${this.selectedSupplier}-`,
      //   this.selectedSupplier != ""
      // );
    },
    onUnApprove(item) {
      if (item.exported) {
        alert("Cannot edit, record has been exported!");
      } else {
        this.$apollo
          .mutate({
            mutation: UNAPPROVE,
            variables: {
              id: item.id,
            },
            refetchQueries: [
              {
                query: APPROVAL_PAYMENTS_LIST,
                variables: { id: this.id },
              },
            ],
          })
          .then(() => {
            EventBus.$emit("reloadPurchaseInvoiceList");
            this.$emit("refresh");
          })
          .catch((err) => {
            const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
            this.$store.dispatch("showAppSnackbar", {
              msg: errorMsg,
              color: "error",
            });
          });
      }
    },
    onCancel() {
      this.$emit("remove", { id: this.id });
      this.onNewInvoice();
    },
    onNewInvoice() {
      this.netAmount = 0;
      this.vatAmount = 0;
      this.totalAmount = 0;
      this.selectedSupplier = "";
      this.selectedHeadOffice = "";
      this.invoiceDueDate = "";
      this.invoiceDate = "";
      this.supplierInvoiceNumber = "";
      this.id = "";
      this.posted = false;
    },
    onNextInvoice() {
      this.netAmount = 0;
      this.vatAmount = 0;
      this.totalAmount = 0;
      this.invoiceDueDate = "";
      this.invoiceDate = "";
      this.supplierInvoiceNumber = "";
      this.id = "";
      this.posted = false;
    },

    deleteItem(item) {
      if (this.posted) {
        alert("Cannot edit!");
      } else {
        this.$apollo
          .mutate({
            mutation: PURCHASE_INVOICE_PAYMENT_DELETE_MUTATION,
            variables: {
              id: item.id,
            },
          })
          .then(() => {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Purchase invoice payment removed.",
            });
            if (this.$apollo.queries.purchaseInvoicePayments != null) {
              this.$apollo.queries.purchaseInvoicePayments.refetch();
            }
          })
          .catch(this.errorHandler);
      }
    },
    errorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
    },
    onPost() {
      this.actionBtnsDisabled = true;
      this.$apollo
        .mutate({
          mutation: UPDATE_APPROVALS,
          variables: {
            input: {
              id: this.id,
              exported: this.posted,
              netAmount: Number(this.netAmount),
              vatAmount: Number(this.vatAmount),
              totalAmount: Number(this.totalAmount),
            },
          },
          refetchQueries: [
            {
              query: APPROVAL_PAYMENTS_LIST,
              variables: { id: this.id },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Approvals Posted.",
          });
          //EventBus.$emit("reloadPurchaseInvoiceList");
          this.$emit("refresh");
          this.actionBtnsDisabled = false;
        })
        .catch(this.errorHandler);
    },
    onNetAmountChange() {
      this.totalAmount = (
        Number(this.netAmount) + Number(this.vatAmount)
      ).toFixed(2);
    },
    onVatAmountChange() {
      this.totalAmount = (
        Number(this.netAmount) + Number(this.vatAmount)
      ).toFixed(2);
    },
    onPurchaseInvoiceEdit(item) {
      this.selectedInvoice = { ...item };
      console.log("invoice date", this.invoiceDate);
      if (this.posted) {
        alert("Cannot edit!");
      } else if (this.invoiceDate == "" || !this.invoiceDate) {
        alert("Select invoice date first!");
      } else if (!this.payeeSelected) {
        alert("Select supplier or head office first!");
      } else if (this.supplierInvoiceNumber != "") {
        this.vatBreakdownRefreshKey = randomize("Aa0", 32);
        this.showVatEditor = true;
      } else {
        this.showSupplierInvoiceNumberDialog = true;
        this.handlerToConfirm = () => {
          this.showVatEditor = randomize("Aa0", 32);
          this.showVatEditor = true;
        };
      }
    },
    onApproveWithoutBreakdown(item) {
      this.selectedInvoice = { ...item };
      if (this.posted) {
        alert("Cannot edit!");
      } else if (this.invoiceDate == "" || !this.invoiceDate) {
        alert("Select invoice date first!");
      } else if (!this.payeeSelected) {
        alert("Select supplier or head office first!");
      } else if (this.supplierInvoiceNumber != "") {
        //this.onApprove([]);
        this.checkAndApprove = true;
      } else {
        this.showSupplierInvoiceNumberDialog = true;
        this.handlerToConfirm = () => (this.checkAndApprove = true);
        //alert("Supplier Invoice Number is required!");
      }
    },
    onApprove(payload) {
      this.showVatEditor = false;
      this.$apollo
        .mutate({
          mutation: CREATE_PI_APPROVALS,
          variables: {
            input: {
              id: this.id && this.id != "" ? this.id : undefined,
              headOfficeId:
                this.selectedHeadOffice != ""
                  ? this.selectedHeadOffice
                  : undefined,
              reference: this.supplierInvoiceNumber,
              purchaseInvoiceId: this.selectedInvoice.id,
              invoiceDate:
                this.invoiceDate != "" ? this.invoiceDate : undefined,
              dueDate:
                this.invoiceDueDate != "" ? this.invoiceDueDate : undefined,
              netAmount: Number(this.netAmount),
              vatAmount: Number(this.vatAmount),
              totalAmount: Number(this.totalAmount),
              vatBreakdown: payload
                .filter((b) => Number(b.grossAmount) != 0)
                .map((b) => ({ ...b, id: undefined })),
            },
          },
          refetchQueries: [
            {
              query: APPROVAL_PAYMENTS_LIST,
              variables: { id: this.id },
            },
          ],
        })
        .then(({ data }) => {
          console.log("result", data);
          this.id = data.createPiApprovals.id;
          this.$store.dispatch("showAppSnackbar", {
            msg: "Approvals created.",
          });
          EventBus.$emit("reloadPurchaseInvoiceList");
          this.$emit("refresh");
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    purchaseInvoicePayments: {
      query: APPROVAL_PAYMENTS_LIST,
      variables() {
        return {
          id: this.id,
        };
      },
      skip() {
        return this.id == "";
      },
      fetchPolicy: "network-only",
    },
    purchaseInvoice: {
      query: PI_VAT_BREAKDOWN,
      fetchPolicy: "network-only",
      variables() {
        return { id: this.selectedInvoice.id };
      },
      skip() {
        //console.log("skip", this.value == null || !this.value.id);
        return (
          this.selectedInvoice == null ||
          !this.selectedInvoice.id ||
          !this.checkAndApprove
        );
      },
      result({ data }) {
        this.checkAndApprove = false;
        const breakdownTotal = data.purchaseInvoice.vatBreakdown.reduce(
          (sum, item) => (sum += Number(item.grossAmount)),
          0
        );
        if (breakdownTotal != Number(this.selectedInvoice.balance)) {
          this.showVatEditor = true;
        } else {
          this.onApprove([]);
        }
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/finances/pi-approvals/PurchaseInvoiceApprovalDialog.vue